import { useEffect } from 'react';
import { useRouter } from 'next/router';

import { Box, Page } from '@zillow/constellation-10';

import { logError } from '@zg-rentals/log-error';
import { useRentalManagerUserPropertyDetailsWebApiHandler_overview } from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { useFeedListingIdRedirect } from '../../../../../hooks';
import { getCachedPropertyAddress } from '../../../../../utils/navigationCacheUtils';

import { ROUTES } from '../../../../../constants/urls';

import { useAuth } from '../../../../../components/AuthProvider';
import RestrictedListingBanner from '../../../../../components/CreateListingPage/RestrictedListingBanner';
import Navigation from '../../../../../components/Dashboard/Navigation';
import PropertyCard from '../../../../../components/Dashboard/PropertyCard';
import RentGuaranteeBannerMultiPolicy from '../../../../../components/Dashboard/RentGuaranteeBannerMultiPolicy';
import { dashboardBodyContentStyles, dashboardHeaderContentStyles } from '../../../../../components/Dashboard/styles';
import OverviewPage from '../../../../../components/OverviewPage';
import AuthCheckWrapper from '../../../../../containers/AuthCheckWrapper';

const DashboardOverviewPage = () => {
  const { isAuthorized } = useAuth();
  const router = useRouter();
  const listingAlias = router.isReady ? (router.query.listingAlias as string) : '';

  const { data: propertyOverviewData } = useRentalManagerUserPropertyDetailsWebApiHandler_overview(
    { listingAlias },
    { enabled: isAuthorized && Boolean(listingAlias) },
  );

  // MFH Redirect Guard - redirect multifamily listings away from dashboard
  useEffect(() => {
    if (propertyOverviewData?.isMultifamily) {
      logError({
        error: JSON.stringify({ listingAlias }),
        errorType: 'IncorrectUILoadingError',
        context: 'DashboardOverviewPage.MFHListingInDashboard',
      });

      void router.push({ pathname: ROUTES.LISTING, query: { listingAlias } });
    }
  }, [propertyOverviewData, listingAlias, router]);

  useFeedListingIdRedirect(listingAlias);

  const cachedAddress = typeof window !== 'undefined' ? getCachedPropertyAddress() : null;

  const skeleton = (
    <Page.Root css={{ backgroundColor: 'bg.softest' }}>
      <Box
        css={{
          backgroundColor: 'bg.neutral',
          borderBottom: '1px solid {colors.border.muted}',
        }}
      >
        <Page.Content css={dashboardHeaderContentStyles}>
          <PropertyCard listingAlias={listingAlias} fallbackAddress={cachedAddress ?? undefined} />
          <Box css={{ marginBottom: '1000' }} />
          <Navigation listingAlias={listingAlias} activeTab="overview" />
        </Page.Content>
      </Box>
    </Page.Root>
  );

  return (
    <AuthCheckWrapper loading={skeleton} redirecting={skeleton}>
      <Page.Root css={{ backgroundColor: 'bg.softest' }}>
        <Box
          css={{
            backgroundColor: 'bg.neutral',
            borderBottom: '1px solid {colors.border.muted}',
          }}
        >
          <RestrictedListingBanner isFromDashboard={true} />
          <Page.Content css={dashboardHeaderContentStyles}>
            <PropertyCard listingAlias={listingAlias} fallbackAddress={cachedAddress ?? undefined} />
            <Box css={{ marginBottom: '1000' }} />
            <Navigation listingAlias={listingAlias} activeTab="overview" />
          </Page.Content>
        </Box>
        <RentGuaranteeBannerMultiPolicy listingAlias={listingAlias} />
        <Page.Content css={dashboardBodyContentStyles}>
          <OverviewPage listingAlias={listingAlias} />
        </Page.Content>
      </Page.Root>
    </AuthCheckWrapper>
  );
};

export default DashboardOverviewPage;
